import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '../lib/supabase'
import { AFAB_ROLES, ADMIN_ROLES, DISPATCH_ROLES, CLIENT_ROLES } from '../types'
import type { Profile, UserRole } from '../types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<any>(null)
  const profile = ref<Profile | null>(null)
  const loading = ref(true)
  const initialized = ref(false)
  const passwordRecovery = ref(false)

  const isLoggedIn = computed(() => !!user.value)

  // Every role this person holds (HOP-167). The dispatcher who schedules local and long
  // range also drives, so "what is this person" has more than one answer.
  const roles = computed<UserRole[]>(() => {
    const list = profile.value?.roles
    if (list?.length) return list
    // A profile read before the column existed, or one mid-deploy. Falling back to the
    // single role keeps such a session working rather than logging it out with no roles.
    return profile.value?.role ? [profile.value.role] : []
  })

  /** Does this person hold any of these roles? The permission question. */
  const hasAnyRole = (...wanted: readonly string[]) =>
    roles.value.some(r => wanted.includes(r))

  // Which page login lands on — the one question that needs a single answer. Kept in the
  // same precedence order as public.primary_role() so the app and the database agree.
  const PRECEDENCE: UserRole[] = ['super_admin', 'admin', 'dispatcher', 'accounting', 'maintenance', 'driver', 'manager', 'viewer']
  const role = computed<UserRole | null>(() =>
    PRECEDENCE.find(r => roles.value.includes(r)) ?? null)

  // Role checks — each now asks whether the person HOLDS the role, not whether it is the
  // only thing they are. isDriver was `role === 'driver'`, which is exactly what excluded
  // the dispatcher who drives.
  const isAfab = computed(() => hasAnyRole(...(AFAB_ROLES as string[])))
  const isAdmin = computed(() => hasAnyRole(...(ADMIN_ROLES as string[])))
  const isDispatcher = computed(() => hasAnyRole(...(DISPATCH_ROLES as string[])))
  const isAccounting = computed(() => hasAnyRole('accounting'))
  const isDriver = computed(() => hasAnyRole('driver'))
  // HOP-130: the shop. Reads the fleet, writes red tags and repair records —
  // not a dispatcher, so isDispatcher stays false and the dispatch nav and
  // routes stay closed to it.
  const isMaintenance = computed(() => hasAnyRole('maintenance'))
  // Long-haul qualification — admin-set; gates the driver Long-Range view.
  const isLongHaulQualified = computed(() => !!profile.value?.long_haul_qualified)
  const isClientManager = computed(() => hasAnyRole('manager'))
  const isClientViewer = computed(() => hasAnyRole('viewer'))
  const isClient = computed(() => hasAnyRole(...(CLIENT_ROLES as string[])))

  // Where to redirect after login
  const homeRoute = computed(() => {
    if (!role.value) return '/login'
    if (isAdmin.value || isDispatcher.value) return '/admin'
    // The fleet is the shop's front door — /admin is the order board, which
    // this role cannot see.
    if (isMaintenance.value) return '/admin/fleet'
    if (isAccounting.value) return '/accounting'
    if (isDriver.value) return '/driver'
    if (isClient.value) return '/client'
    return '/login'
  })

  async function fetchProfile() {
    if (!user.value) return
    const { data } = await supabase
      .from('profiles')
      .select('*, organization:organizations(id, name)')
      .eq('id', user.value.id)
      .single()
    profile.value = data
  }

  async function init() {
    loading.value = true
    const { data: { session } } = await supabase.auth.getSession()
    user.value = session?.user ?? null
    if (user.value) {
      await fetchProfile()
      if (profile.value?.disabled) {
        await supabase.auth.signOut()
        user.value = null
        profile.value = null
      }
    }
    loading.value = false
    initialized.value = true

    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') return
      if (event === 'PASSWORD_RECOVERY') {
        passwordRecovery.value = true
      }
      user.value = session?.user ?? null
      // CRITICAL: never await any supabase call inside this callback.
      // The auth client holds an internal lock while the callback runs;
      // calling supabase.* synchronously here re-enters the lock and
      // deadlocks every subsequent supabase call in the app (queries
      // hang, tabs return to a stuck UI, etc.). Defer with setTimeout(0)
      // so the callback returns and the lock releases first.
      // See: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
      if (user.value) {
        setTimeout(() => { fetchProfile() }, 0)
      } else {
        profile.value = null
      }
    })
  }

  async function login(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    await fetchProfile()
    if (profile.value?.disabled) {
      await supabase.auth.signOut()
      user.value = null
      profile.value = null
      throw new Error('Your account has been disabled. Contact an administrator.')
    }
  }

  async function signUpWithInvite(
    email: string,
    password: string,
    fullName: string,
    inviteRole: UserRole,
    organizationId: string | null,
    inviteToken: string
  ) {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role: inviteRole,
          organization_id: organizationId,
        }
      }
    })
    if (error) throw error

    // Mark invite as accepted
    await supabase.rpc('accept_invite', { invite_token: inviteToken })
  }

  async function logout() {
    user.value = null
    profile.value = null
    await supabase.auth.signOut()
  }

  return {
    user, profile, loading, passwordRecovery,
    isLoggedIn, role, roles, hasAnyRole, homeRoute,
    isAfab, isAdmin, isDispatcher, isAccounting, isDriver, isMaintenance, isLongHaulQualified,
    isClientManager, isClientViewer, isClient,
    init, initialized, login, signUpWithInvite, logout, fetchProfile,
  }
})
